import { Heading, WoundDressingIcon } from '@lmc/design-system'
import { cssVars } from '@lmc/design-system/tokens'
import type { BodyMapPickerReport } from '@lmc/validations'
import { InjuredPersonTypeEnum } from '@lmc/validations'
import useBodyMaps from 'hooks/useBodyMaps'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import BodyMapItem from '../../../bodyMaps/BodyMapItem'
import StepEntryCard, {
  StepEntryCardItem,
  StepEntryCardRow,
  StepEntryList,
  useEntryDrawer
} from '../../StepEntryCard'
import type { StepOption } from '../../stepOptions'
import InjuryDrawer from './InjuryDrawer'
import {
  hasPerson,
  type InjuredPeopleOptions,
  type InjuryEntryValue
} from './injuryEntryValues'

type InjuryEntriesProps = {
  /** Everyone in the home, offered once People involved has no one left. */
  homePeople: Pick<InjuredPeopleOptions, 'otherServiceUsers' | 'teamMembers'>
  /** Every name the step can show, by resident or user id. */
  labels: Map<string, string>
  onChange?: (entries: Array<InjuryEntryValue>) => void
  /** Who can be recorded as injured, before those already recorded are removed. */
  people: InjuredPeopleOptions
  serviceUser: { id: string; name: string }
  /** The home's words for a service user. */
  serviceUserWords: { plural: string; singular: string }
  showBodyMaps: boolean
  teamMemberOptions: Array<StepOption>
  testId: string
  value?: Array<InjuryEntryValue>
}

type Group = {
  addKey: string
  personType: InjuredPersonTypeEnum
  titleKey: string
}

const GROUPS: ReadonlyArray<Group> = [
  {
    addKey: incidentReportsConstants.CASE_V2_INJURIES_ADD_SERVICE_USER_INJURIES,
    personType: InjuredPersonTypeEnum.serviceUser,
    titleKey: incidentReportsConstants.CASE_V2_INJURIES_SERVICE_USER
  },
  {
    addKey: incidentReportsConstants.CASE_V2_INJURIES_ADD_OTHER_SERVICE_USER,
    personType: InjuredPersonTypeEnum.otherServiceUser,
    titleKey: incidentReportsConstants.CASE_V2_INJURIES_OTHER_SERVICE_USERS
  },
  {
    addKey: incidentReportsConstants.CASE_V2_INJURIES_ADD_TEAM_MEMBER,
    personType: InjuredPersonTypeEnum.teamMember,
    titleKey: incidentReportsConstants.CASE_V2_INJURIES_TEAM_MEMBERS
  },
  {
    addKey: incidentReportsConstants.CASE_V2_INJURIES_ADD_PARTY,
    personType: InjuredPersonTypeEnum.otherParty,
    titleKey: incidentReportsConstants.CASE_V2_INJURIES_OTHER_PARTIES
  }
]

/**
 * The injuries recorded per person, grouped as designed: the case's own
 * service user (once), other service users, team members and other parties.
 * A drawer adds or edits one; each saved injury shows as a card.
 */
const InjuryEntries = ({
  homePeople,
  labels,
  onChange,
  people,
  serviceUser,
  serviceUserWords,
  showBodyMaps,
  teamMemberOptions,
  testId,
  value = []
}: InjuryEntriesProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { close, drawer, open, remove, save } = useEntryDrawer(value, onChange)

  const nameOf = (entry: InjuryEntryValue) => {
    if (!hasPerson(entry)) {
      return t(incidentReportsConstants.CASE_V2_INJURIES_UNNAMED)
    }
    switch (entry.personType) {
      case InjuredPersonTypeEnum.serviceUser:
        return serviceUser.name
      case InjuredPersonTypeEnum.otherServiceUser:
        return labels.get(entry.residentId ?? '') ?? ''
      case InjuredPersonTypeEnum.teamMember:
        return labels.get(entry.userId ?? '') ?? ''
      default:
        return entry.name ?? ''
    }
  }

  /** Each person once: those already recorded are not offered again. */
  const availablePeople = (
    editing?: InjuryEntryValue
  ): InjuredPeopleOptions => {
    const taken = new Set<string | undefined>(
      value
        .filter((entry) => entry !== editing)
        .flatMap((entry) => [entry.residentId, entry.userId])
    )
    const untaken = (options: Array<StepOption>) =>
      options.filter((option) => !taken.has(option.value))
    // Once everyone involved has an injury, the whole home, as when nobody
    // of that kind was involved.
    const offer = (involved: Array<StepOption>, home: Array<StepOption>) => {
      const left = untaken(involved)
      return left.length ? left : untaken(home)
    }
    return {
      ...people,
      otherServiceUsers: offer(
        people.otherServiceUsers,
        homePeople.otherServiceUsers
      ),
      teamMembers: offer(people.teamMembers, homePeople.teamMembers)
    }
  }

  const hasServiceUserEntry = value.some(
    (entry) => entry.personType === InjuredPersonTypeEnum.serviceUser
  )

  return (
    <Groups data-testid={testId}>
      {GROUPS.map(({ addKey, personType, titleKey }) => (
        <Group key={personType} data-testid={`${testId}-${personType}`}>
          <Heading size="xs" weight="medium">
            {t(titleKey, {
              variable:
                personType === InjuredPersonTypeEnum.otherServiceUser
                  ? serviceUserWords.plural
                  : serviceUserWords.singular
            })}
          </Heading>
          <StepEntryList
            addLabel={t(addKey, { variable: serviceUserWords.singular })}
            addTestId={`${testId}-add-${personType}`}
            onAdd={
              personType === InjuredPersonTypeEnum.serviceUser &&
              hasServiceUserEntry
                ? undefined
                : () => open({ personType })
            }
          >
            {value.map((entry, index) =>
              entry.personType === personType ? (
                <StepEntryCard
                  // Entries have no id; the list is replaced whole on save.
                  // biome-ignore lint/suspicious/noArrayIndexKey: see above
                  key={index}
                  deleteLabel={t(
                    incidentReportsConstants.CASE_V2_INJURIES_DELETE,
                    { name: nameOf(entry) }
                  )}
                  editLabel={t(incidentReportsConstants.CASE_V2_INJURIES_EDIT, {
                    name: nameOf(entry)
                  })}
                  error={
                    hasPerson(entry)
                      ? undefined
                      : t(
                          incidentReportsConstants.CASE_V2_INJURIES_CHOOSE_PERSON
                        )
                  }
                  icon={<WoundDressingIcon size={24} />}
                  title={nameOf(entry)}
                  testId={`${testId}-entry-${index}`}
                  onEdit={() => open(entry, index)}
                  onDelete={() => remove(index)}
                >
                  <InjuryDetails
                    entry={entry}
                    labels={labels}
                    residentId={serviceUser.id}
                    showBodyMaps={showBodyMaps}
                  />
                </StepEntryCard>
              ) : null
            )}
          </StepEntryList>
        </Group>
      ))}
      {drawer && (
        <InjuryDrawer
          entry={drawer.entry}
          isNew={drawer.index === undefined}
          nominative={serviceUserWords.singular}
          onClose={close}
          onConfirm={save}
          people={availablePeople(
            drawer.index === undefined ? undefined : value[drawer.index]
          )}
          serviceUser={serviceUser}
          showBodyMaps={showBodyMaps}
          teamMemberOptions={teamMemberOptions}
          testId={`${testId}-drawer`}
        />
      )}
    </Groups>
  )
}

type InjuryDetailsProps = {
  entry: InjuryEntryValue
  labels: Map<string, string>
  residentId: string
  showBodyMaps: boolean
}

/** What a card shows under the injured person's name. */
const InjuryDetails = ({
  entry,
  labels,
  residentId,
  showBodyMaps
}: InjuryDetailsProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

  return (
    <>
      {showBodyMaps && entry.bodyMaps?.length ? (
        <LinkedBodyMaps ids={entry.bodyMaps} residentId={residentId} />
      ) : null}
      {entry.details && (
        <StepEntryCardItem
          label={t(incidentReportsConstants.CASE_V2_INJURIES_DETAILS)}
        >
          {entry.details}
        </StepEntryCardItem>
      )}
      {entry.treatmentGiven !== undefined && (
        <StepEntryCardRow>
          <StepEntryCardItem
            label={t(incidentReportsConstants.CASE_V2_INJURIES_TREATMENT_GIVEN)}
          >
            {t(
              entry.treatmentGiven
                ? incidentReportsConstants.CASE_V2_YES
                : incidentReportsConstants.CASE_V2_NO
            )}
          </StepEntryCardItem>
          {entry.treatmentGiven && entry.treatmentGivenBy && (
            <StepEntryCardItem
              label={t(incidentReportsConstants.CASE_V2_INJURIES_BY_WHOM)}
            >
              {labels.get(entry.treatmentGivenBy) ?? ''}
            </StepEntryCardItem>
          )}
        </StepEntryCardRow>
      )}
      {entry.treatmentGiven && entry.treatment && (
        <StepEntryCardItem
          label={t(incidentReportsConstants.CASE_V2_INJURIES_TREATMENT)}
        >
          {entry.treatment}
        </StepEntryCardItem>
      )}
    </>
  )
}

type LinkedBodyMapsProps = {
  ids: Array<string>
  residentId: string
}

/**
 * The body maps linked to the service user's injury, read-only. Loading, a
 * failed load and a map that no longer resolves each say so rather than vanish.
 */
const LinkedBodyMaps = ({ ids, residentId }: LinkedBodyMapsProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { bodyMaps, isError, isLoading } = useBodyMaps({ residentId })

  const status = isLoading
    ? incidentReportsConstants.CASE_V2_INJURIES_BODY_MAPS_LOADING
    : isError && incidentReportsConstants.CASE_V2_INJURIES_BODY_MAPS_NOT_LOADED

  return (
    <StepEntryCardItem
      label={t(incidentReportsConstants.CASE_V2_INJURIES_RELATED_BODY_MAPS)}
    >
      {status ? (
        t(status)
      ) : (
        <BodyMapList>
          {ids.map((id) => {
            const bodyMap = bodyMaps.find((map) => String(map.id) === id)

            return bodyMap ? (
              <BodyMapItem
                key={id}
                report={
                  {
                    createdOn: new Date(bodyMap.createdOn).toISOString(),
                    direction: null,
                    id,
                    lastActivityOn: null,
                    lastComment: null,
                    lastDescription: null,
                    lastLoggedBy: null,
                    resolved: Boolean(bodyMap.resolved),
                    section: null,
                    title: bodyMap.title ?? null
                  } satisfies BodyMapPickerReport
                }
                showCheckbox={false}
              />
            ) : (
              <span key={id}>
                {t(
                  incidentReportsConstants.CASE_V2_INJURIES_BODY_MAP_NOT_FOUND
                )}
              </span>
            )
          })}
        </BodyMapList>
      )}
    </StepEntryCardItem>
  )
}

const { spacing } = cssVars

const Groups = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[300]};
`

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[150]};
`

const BodyMapList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[100]};
`

export default InjuryEntries
